'use client';

import { useEffect, useRef, useState } from 'react';
import { PencilIcon, PlusIcon, Trash2Icon } from 'lucide-react';
import {
  type Memory,
  MemoryRequestError,
  MEMORY_MAX_CHARS,
  createMemory,
  deleteMemory,
  listMemories,
  updateMemory,
} from '@/settings';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { phoneSafeInputTextClass, textInputClass } from '@/components/ui/form-control';
import { OverflowMenu } from '@/components/ui/menu';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import { overlaysCopy } from '@/i18n/ns/overlays';
import { cn } from '@/lib/utils';
import { interpolateCopy, useChatCopy, useChatRequestedLocale } from '@/lib/chat-i18n';
import { formatAppDate } from '@/i18n/format';

const inputClass = cn(textInputClass, 'min-h-9 rounded-lg px-2.5', phoneSafeInputTextClass);
const labelClass = 'text-sm font-medium';
const helperClass = 'text-muted-foreground text-xs leading-snug';
const secondaryButton =
  'border-border hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring ' +
  'inline-flex min-h-9 items-center justify-center gap-1.5 rounded-lg border px-3 text-sm ' +
  'font-medium transition-colors focus-visible:outline-none disabled:opacity-50';

export interface MemoriesApi {
  list: () => Promise<Memory[]>;
  create: (content: string) => Promise<Memory>;
  update: (id: string, content: string) => Promise<Memory>;
  delete: (id: string) => Promise<void>;
}

const defaultApi: MemoriesApi = {
  list: listMemories,
  create: createMemory,
  update: updateMemory,
  delete: deleteMemory,
};

function memorySaveError(error: unknown, copy: ReturnType<typeof useChatCopy>): string {
  if (error instanceof MemoryRequestError) {
    if (error.code === 'memory_limit') return copy.memoryLimitReached;
    if (error.status === 400) return copy.memoryInvalid;
  }
  return copy.memorySaveFailed;
}

function MemoryEditor({
  initialValue,
  label,
  busy,
  error,
  onCancel,
  onSave,
}: {
  initialValue: string;
  label: string;
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onSave: (content: string) => void;
}) {
  const copy = useChatCopy();
  const [text, setText] = useState(initialValue);
  const trimmed = text.trim();

  return (
    <div className="border-border flex flex-col gap-2 rounded-xl border p-3">
      <label className={labelClass}>
        {label}
        <textarea
          autoFocus
          value={text}
          maxLength={MEMORY_MAX_CHARS}
          disabled={busy}
          onInput={(event) => setText((event.currentTarget as HTMLTextAreaElement).value)}
          placeholder={copy.memoryPlaceholder}
          className={inputClass + ' mt-1.5 min-h-24 w-full resize-y p-2.5'}
        />
      </label>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-muted-foreground text-xs tabular-nums">
          {text.length} / {MEMORY_MAX_CHARS}
        </span>
        {error && (
          <span className="text-destructive text-xs" role="alert">
            {error}
          </span>
        )}
        <div className="ml-auto flex gap-2">
          <button type="button" className={secondaryButton} disabled={busy} onClick={onCancel}>
            {copy.cancel}
          </button>
          <button
            type="button"
            className="bg-foreground text-background focus-visible:ring-foreground inline-flex min-h-9 items-center justify-center rounded-lg px-3 text-sm font-semibold focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:opacity-50"
            disabled={busy || trimmed.length === 0}
            onClick={() => onSave(trimmed)}
          >
            {copy.saveMemory}
          </button>
        </div>
      </div>
    </div>
  );
}

/** A short, readable name for a memory in its ⋯ label ("More actions for I prefer short…"). */
function memoryName(content: string) {
  const name = content.trim();
  return name.length > 48 ? `${name.slice(0, 47).trimEnd()}…` : name;
}

function MemoryRow({
  memory,
  api,
  onSaved,
  onDeleted,
}: {
  memory: Memory;
  api: MemoriesApi;
  onSaved: (memory: Memory) => void;
  onDeleted: (id: string) => void;
}) {
  const copy = useChatCopy();
  const overlays = useNamespaceCopy(overlaysCopy);
  const locale = useChatRequestedLocale();
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  async function save(content: string) {
    setBusy(true);
    setError(null);
    try {
      const next = await api.update(memory.id, content);
      if (!mounted.current) return;
      onSaved(next);
      setEditing(false);
    } catch (caught) {
      if (mounted.current) setError(memorySaveError(caught, copy));
    } finally {
      if (mounted.current) setBusy(false);
    }
  }

  async function remove() {
    setBusy(true);
    setError(null);
    try {
      await api.delete(memory.id);
      if (!mounted.current) return;
      onDeleted(memory.id);
    } catch {
      if (mounted.current) {
        setError(copy.memoryDeleteFailed);
        setBusy(false);
        setConfirming(false);
      }
    }
  }

  if (editing) {
    return (
      <MemoryEditor
        initialValue={memory.content}
        label={copy.editMemory}
        busy={busy}
        error={error}
        onCancel={() => {
          setEditing(false);
          setError(null);
        }}
        onSave={(content) => void save(content)}
      />
    );
  }

  return (
    <article className="border-border flex items-start gap-2 rounded-xl border p-3">
      <div className="min-w-0 flex-1">
        <div className="text-sm leading-snug break-words">{memory.content}</div>
        <div className="text-muted-foreground mt-1 text-xs">
          {interpolateCopy(copy.saved, { date: formatAppDate(memory.createdAt, locale) })}
        </div>
        {error && (
          <div className="mt-1 text-destructive text-xs" role="alert">
            {error}
          </div>
        )}
      </div>
      {/* A memory reads as text; editing and deleting are one step away (PL-31). */}
      <OverflowMenu
        label={formatCopy(overlays.moreActionsFor, { name: memoryName(memory.content) })}
        items={[
          {
            key: 'edit',
            label: copy.editMemory,
            icon: <PencilIcon aria-hidden="true" />,
            disabled: busy,
            onSelect: () => setEditing(true),
          },
          {
            key: 'delete',
            label: copy.deleteMemory,
            icon: <Trash2Icon aria-hidden="true" />,
            tone: 'danger',
            separated: true,
            disabled: busy,
            onSelect: () => setConfirming(true),
          },
        ]}
      />
      {/* Deleting cannot be undone, so it asks first and names the memory (§7.1). */}
      {confirming && (
        <ConfirmDialog
          title={overlays.deleteMemoryTitle}
          description={formatCopy(overlays.deleteMemoryDescription, {
            name: memoryName(memory.content),
          })}
          confirmLabel={copy.deleteMemory}
          busy={busy}
          onCancel={() => setConfirming(false)}
          onConfirm={() => void remove()}
        />
      )}
    </article>
  );
}

export function MemoriesSection({
  open,
  signedIn,
  api = defaultApi,
}: {
  open: boolean;
  signedIn: boolean;
  api?: MemoriesApi;
}) {
  const copy = useChatCopy();
  const [memories, setMemories] = useState<Memory[] | null>(null);
  const [adding, setAdding] = useState(false);
  const [createBusy, setCreateBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lifecycle = useRef(0);

  useEffect(() => {
    const epoch = ++lifecycle.current;
    setMemories(null);
    setAdding(false);
    setCreateBusy(false);
    setError(null);
    if (!open || !signedIn) {
      return;
    }
    void api
      .list()
      .then((next) => {
        if (lifecycle.current === epoch) setMemories(next);
      })
      .catch(() => {
        if (lifecycle.current === epoch) setError(copy.memoryLoadFailed);
      });
    return () => {
      if (lifecycle.current === epoch) lifecycle.current += 1;
    };
  }, [api, copy.memoryLoadFailed, open, signedIn]);

  async function add(content: string) {
    const epoch = lifecycle.current;
    setCreateBusy(true);
    setError(null);
    try {
      const memory = await api.create(content);
      if (lifecycle.current !== epoch) return;
      setMemories((current) => [...(current ?? []), memory]);
      setAdding(false);
    } catch (caught) {
      if (lifecycle.current === epoch) setError(memorySaveError(caught, copy));
    } finally {
      if (lifecycle.current === epoch) setCreateBusy(false);
    }
  }

  let body;
  if (!signedIn) {
    body = <div className={helperClass}>{copy.loginToUseMemories}.</div>;
  } else if (memories === null && !error) {
    body = <div className={helperClass}>{copy.loadingMemories}</div>;
  } else {
    body = (
      <div className="flex flex-col gap-2">
        {memories?.length === 0 && !adding ? (
          <div className={helperClass}>{copy.emptyMemories}</div>
        ) : null}
        {adding ? (
          <MemoryEditor
            initialValue=""
            label={copy.newMemory}
            busy={createBusy}
            error={error}
            onCancel={() => {
              setAdding(false);
              setError(null);
            }}
            onSave={(content) => void add(content)}
          />
        ) : null}
        {memories?.map((memory) => (
          <MemoryRow
            key={memory.id}
            memory={memory}
            api={api}
            onSaved={(next) =>
              setMemories((current) =>
                current!.map((candidate) => (candidate.id === next.id ? next : candidate)),
              )
            }
            onDeleted={(id) =>
              setMemories((current) => current!.filter((candidate) => candidate.id !== id))
            }
          />
        ))}
        {error && !adding ? (
          <div className="text-destructive text-xs" role="alert">
            {error}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="border-border flex flex-col gap-3 border-t pt-4">
      <div className="flex items-center justify-between gap-3">
        <span className={labelClass}>{copy.memories}</span>
        {signedIn && memories !== null ? (
          <button
            type="button"
            className={secondaryButton}
            disabled={adding || createBusy}
            onClick={() => {
              setAdding(true);
              setError(null);
            }}
          >
            <PlusIcon className="size-4" aria-hidden="true" />
            {copy.addMemory}
          </button>
        ) : null}
      </div>
      {body}
    </div>
  );
}
